import type { JSX } from "react";
import { cn } from "./cn";
import { Icon } from "./Icon";
import type { IconName } from "./Icon";
import { EngagementChannelChip } from "./EngagementChannelChip";
import type { EngagementChannelColor } from "./EngagementChannelChip";

export type EngagementStatusColor = "accent" | "blue" | "purple" | "green" | "amber";

export interface EngagementChannel {
  readonly label: string;
  readonly color?: EngagementChannelColor;
}

export interface EngagementCardProps {
  /** Leading icon name */
  readonly icon: IconName;
  /** Icon/text color theme */
  readonly iconColor?: EngagementStatusColor;
  /** Card title */
  readonly title: string;
  /** Status badge text (e.g. "ACTIVE", "IN PROGRESS") */
  readonly status: string;
  /** Status badge color */
  readonly statusColor?: EngagementStatusColor;
  /** Description text */
  readonly description: string;
  /** Channel chips (Telegram, Email, etc.) */
  readonly channels?: readonly EngagementChannel[];
  /** Click handler */
  readonly onClick?: () => void;
  /** Extra className */
  readonly className?: string;
}

const iconColorMap: Readonly<Record<EngagementStatusColor, string>> = {
  accent: "text-accent",
  blue: "text-blue",
  purple: "text-purple",
  green: "text-green",
  amber: "text-amber",
};

const statusColorMap: Readonly<Record<EngagementStatusColor, string>> = {
  accent: "bg-accent/20 text-accent",
  blue: "bg-blue/20 text-blue",
  purple: "bg-purple/20 text-purple",
  green: "bg-green/20 text-green",
  amber: "bg-amber/20 text-amber",
};

/**
 * Engagement/project card with icon, title, status badge, description, and channel chips.
 * Matches B1 design — reusable for partnerships, tasks, collaborations.
 */
export function EngagementCard({
  icon,
  iconColor = "accent",
  title,
  status,
  statusColor,
  description,
  channels,
  onClick,
  className,
}: EngagementCardProps): JSX.Element {
  const resolvedStatusColor = statusColor ?? iconColor;
  const Component = onClick ? "button" : "div";
  return (
    <Component
      type={onClick ? "button" : undefined}
      className={cn(
        "flex flex-col gap-2 bg-surface-secondary rounded-[10px] px-4 py-3.5",
        "border border-edge",
        onClick && "cursor-pointer hover:bg-surface-hover transition-colors text-left w-full",
        className,
      )}
      onClick={onClick}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className={cn("shrink-0", iconColorMap[iconColor])}>
          <Icon name={icon} size={16} />
        </span>
        <span className="flex-1 text-[13px] font-medium text-[#E8EDEF] truncate">{title}</span>
        <span className="flex-1 min-w-0" aria-hidden />
        <span
          className={cn(
            "shrink-0 px-2 py-[3px] rounded-md text-[10px] font-semibold uppercase",
            statusColorMap[resolvedStatusColor],
          )}
        >
          {status}
        </span>
      </div>
      <p className="text-xs text-content-muted leading-[1.4]">{description}</p>
      {channels && channels.length > 0 ? (
        <div className="flex items-center gap-1.5 flex-wrap">
          {channels.map((ch) => (
            <EngagementChannelChip key={ch.label} label={ch.label} color={ch.color} />
          ))}
        </div>
      ) : null}
    </Component>
  );
}
